import React, { useState } from 'react';
import { 
  Building2, 
  Search, 
  Plus, 
  Phone, 
  Mail, 
  MapPin, 
  DollarSign, 
  Tag, 
  ExternalLink, 
  MessageCircle, 
  Zap, 
  Check, 
  X, 
  Trash2, 
  Edit3, 
  ArrowLeft, 
  ShieldCheck, 
  FileText, 
  ScanLine, 
  ShoppingBag, 
  ChevronRight,
  Filter,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { Supplier, SupplierSource } from '../types';
import { fetchCompanyByCnpj, fetchAddressByCep, CompanyResult } from '../services/publicDataServices';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';

interface SupplierManagerProps {
  suppliers: Supplier[];
  onSaveSupplier: (supplier: Supplier) => Promise<void> | void;
  onDeleteSupplier?: (supplierId: string) => Promise<void> | void;
  onBack: () => void;
}

const SUPPLIER_CATEGORIES = [
  'Autopeças & Mecânica',
  'Material Elétrico & Iluminação',
  'Tubos, Conexões & Hidráulica',
  'Climatização & Refrigeração',
  'Cosméticos & Estética',
  'Madeiras, Ferragens & MDF',
  'Chapas Acrílico & Laser',
  'Tintas & Abrasivos',
  'Embalagens & Descartáveis',
  'Geral & Outros'
];

export default function SupplierManager({
  suppliers,
  onSaveSupplier,
  onDeleteSupplier,
  onBack
}: SupplierManagerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Partial<Supplier> | null>(null);
  
  // Estados de Busca Automática (Zero Digitação)
  const [isSearchingCnpj, setIsSearchingCnpj] = useState(false);
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  const [autoFillMessage, setAutoFillMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const maskPhone = (val: string) => {
    let digits = val.replace(/\D/g, '');
    if (digits.startsWith('0') && (digits.length === 11 || digits.length === 12)) digits = digits.slice(1);
    if (digits.startsWith('55') && digits.length > 11) digits = digits.slice(2);
    if (digits.length > 11) digits = digits.slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const maskCnpj = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  };

  const generateUuid = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Filtragem
  const filteredSuppliers = suppliers.filter(s => {
    const matchesSearch = 
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.tradeName && s.tradeName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (s.cnpj && s.cnpj.includes(searchTerm)) ||
      (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (s.address && s.address.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = 
      selectedCategory === 'all' || s.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const handleOpenNewModal = () => {
    setEditingSupplier({
      id: generateUuid(),
      userId: 'current_user',
      name: '',
      tradeName: '',
      cnpj: '',
      category: 'Autopeças & Mecânica',
      phone: '',
      email: '',
      address: '',
      paymentTerms: 'Boleto 28 ddl ou PIX com desconto',
      rating: 5,
      source: 'manual',
      notes: ''
    });
    setAutoFillMessage(null);
    setIsEditModalOpen(true);
  };

  const handleOpenEditModal = (supplier: Supplier) => {
    setEditingSupplier({ ...supplier });
    setAutoFillMessage(null);
    setIsEditModalOpen(true);
  };

  const handleDeleteSupplierAction = async (supplierId: string, supplierName?: string) => {
    if (!onDeleteSupplier) return;
    const confirmDelete = window.confirm(`Tem certeza que deseja excluir o fornecedor "${supplierName || 'selecionado'}"?\nEsta ação não poderá ser desfeita.`);
    if (!confirmDelete) return;

    try {
      await onDeleteSupplier(supplierId);
      if (selectedSupplier && selectedSupplier.id === supplierId) {
        setSelectedSupplier(null);
      }
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Erro ao excluir fornecedor:', err);
      alert('Erro ao tentar excluir fornecedor.');
    }
  };

  // Busca Inteligente de CNPJ na Receita Federal
  const handleAutoFillCnpj = async () => {
    if (!editingSupplier?.cnpj) {
      setAutoFillMessage({ type: 'error', text: 'Digite o CNPJ para consultar os dados da empresa.' });
      return;
    }

    setIsSearchingCnpj(true);
    setAutoFillMessage(null);

    const result: CompanyResult = await fetchCompanyByCnpj(editingSupplier.cnpj);
    setIsSearchingCnpj(false);

    if (result.success) {
      setEditingSupplier(prev => ({
        ...prev,
        cnpj: result.cnpj,
        name: result.razaoSocial,
        tradeName: result.nomeFantasia || result.razaoSocial,
        address: result.formattedAddress,
        phone: result.phone ? maskPhone(result.phone) : (prev?.phone || ''),
        email: result.email || prev?.email || '',
        cnae: result.cnae,
        source: 'cnpj_lookup',
        notes: prev?.notes ? `${prev.notes}\nSituação: ${result.situacaoCadastral}` : `Situação Cadastral: ${result.situacaoCadastral}`
      }));
      setAutoFillMessage({ type: 'success', text: 'Dados da empresa carregados com sucesso!' });
    } else {
      setAutoFillMessage({ type: 'error', text: result.error || 'Erro ao consultar CNPJ.' });
    }
  };

  const handleSaveForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSupplier || !editingSupplier.name?.trim()) {
      alert('Por favor, informe a Razão Social ou Nome do fornecedor.');
      return;
    }

    const compiled: Supplier = {
      id: editingSupplier.id || `sup_${Date.now()}`,
      userId: editingSupplier.userId || 'current_user',
      name: editingSupplier.name.trim(),
      tradeName: editingSupplier.tradeName?.trim() || undefined,
      cnpj: editingSupplier.cnpj?.trim() || undefined,
      ie: editingSupplier.ie?.trim() || undefined,
      cnae: editingSupplier.cnae?.trim() || undefined,
      category: editingSupplier.category || 'Geral & Outros',
      phone: editingSupplier.phone?.trim() || undefined,
      email: editingSupplier.email?.trim() || undefined,
      address: editingSupplier.address?.trim() || undefined,
      paymentTerms: editingSupplier.paymentTerms?.trim() || undefined,
      rating: editingSupplier.rating || 5,
      notes: editingSupplier.notes?.trim() || undefined,
      source: (editingSupplier.source as SupplierSource) || 'manual',
      suppliedProductsCount: editingSupplier.suppliedProductsCount || 0,
      totalPurchased: editingSupplier.totalPurchased || 0,
      createdAt: editingSupplier.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await onSaveSupplier(compiled);
    setIsEditModalOpen(false);
    if (selectedSupplier && selectedSupplier.id === compiled.id) {
      setSelectedSupplier(compiled);
    }
  };

  const handleOpenWhatsApp = (phone?: string, name?: string) => {
    if (!phone) {
      alert('Telefone do fornecedor não cadastrado.');
      return;
    }
    const cleanPhone = phone.replace(/\D/g, '');
    const finalPhone = cleanPhone.startsWith('55') && cleanPhone.length > 11 ? cleanPhone : `55${cleanPhone}`;
    const text = `Olá, *${name || 'Fornecedor'}*! Gostaria de consultar a disponibilidade e preço de insumos/peças.`;
    window.open(`https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleOpenGoogleMaps = (address: string) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };

  const getSourceBadge = (source: SupplierSource) => {
    switch (source) {
      case 'nfe_upload':
      case 'nfc_scanner':
        return { label: 'Via Nota Fiscal Eletrônica', bg: 'bg-purple-50 text-purple-800 border-purple-200', icon: ScanLine };
      case 'cnpj_lookup':
        return { label: 'Cadastro Empresarial', bg: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: Zap };
      default:
        return { label: 'Manual', bg: 'bg-zinc-100 text-zinc-700 border-zinc-200', icon: Building2 };
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 pb-20 font-sans w-full max-w-full overflow-x-hidden">
      {/* Header Sticky */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-100 px-3 sm:px-6 py-3 flex items-center justify-between shadow-sm gap-2">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <button
            type="button"
            onClick={onBack}
            className="p-2 hover:bg-zinc-100 text-zinc-700 rounded-xl transition-colors cursor-pointer shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black text-brand uppercase tracking-widest bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 truncate">
                Materiais e Insumos
              </span>
              <span className="text-xs text-zinc-400 font-bold hidden sm:inline">
                {suppliers.length} {suppliers.length === 1 ? 'fornecedor parceiro' : 'fornecedores parceiros'}
              </span>
            </div>
            <h1 className="text-sm sm:text-lg font-bold text-zinc-900 truncate">
              Gestão de Fornecedores
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <ScreenTourButton screenKey="suppliers" />
          <button
            type="button"
            onClick={handleOpenNewModal}
            className="px-3 sm:px-4 py-2 sm:py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm shadow-brand/20 flex items-center gap-1.5 cursor-pointer active:scale-95"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Novo Fornecedor</span>
            <span className="sm:hidden">Novo</span>
          </button>
        </div>
      </div>

      {/* Barra de Busca & Categorias */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-5 space-y-3">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Buscar por Razão Social, Nome Fantasia, CNPJ ou Categoria..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-white border border-zinc-100 rounded-2xl pl-10 pr-4 py-3 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 shadow-xs"
          />
        </div>

        {/* Filtros em Pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none text-xs">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={`px-3.5 py-1.5 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap text-xs ${
              selectedCategory === 'all'
                ? 'bg-brand text-white shadow-xs'
                : 'bg-white text-zinc-600 border border-zinc-100 hover:bg-zinc-50'
            }`}
          >
            Todos ({suppliers.length})
          </button>

          {SUPPLIER_CATEGORIES.map(cat => {
            const count = suppliers.filter(s => s.category === cat).length;
            if (count === 0 && selectedCategory !== cat) return null;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-3.5 py-1.5 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap text-xs ${
                  selectedCategory === cat
                    ? 'bg-brand text-white shadow-xs'
                    : 'bg-white text-zinc-600 border border-zinc-100 hover:bg-zinc-50'
                }`}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Lista de Fornecedores */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
        {filteredSuppliers.length === 0 ? (
          <div className="py-12 bg-white border border-dashed border-zinc-200 rounded-3xl text-center p-6 flex flex-col items-center justify-center">
            <Building2 className="h-10 w-10 text-zinc-300 mb-2" />
            <p className="text-xs font-bold text-zinc-700">Nenhum fornecedor encontrado.</p>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              Cadastre novos fornecedores pelo CNPJ ou faça a leitura de Notas Fiscais no Centro de Custos.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3.5">
            {filteredSuppliers.map(sup => {
              const sourceBadge = getSourceBadge(sup.source);
              const SourceIcon = sourceBadge.icon;

              return (
                <div
                  key={sup.id}
                  onClick={() => setSelectedSupplier(sup)}
                  className="p-4 sm:p-5 bg-white border border-zinc-100 hover:border-zinc-200 rounded-2xl space-y-3.5 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between active:scale-98"
                >
                  <div className="space-y-2.5">
                    {/* Header do Card */}
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-0.5 rounded-full border ${sourceBadge.bg}`}>
                        <SourceIcon className="h-3 w-3" />
                        {sourceBadge.label}
                      </span>

                      <span className="text-[9px] font-bold bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-md border border-zinc-100">
                        {sup.category}
                      </span>
                    </div>

                    {/* Razão e Fantasia */}
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-brand/10 text-brand-dark flex items-center justify-center font-black text-sm shrink-0 border border-brand/20 group-hover:scale-105 transition-transform">
                        {sup.tradeName ? sup.tradeName.substring(0, 2).toUpperCase() : sup.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-bold text-zinc-900 group-hover:text-brand transition-colors truncate">
                          {sup.tradeName || sup.name}
                        </h3>
                        {sup.tradeName && sup.tradeName !== sup.name && (
                          <p className="text-[11px] text-zinc-400 truncate font-medium">
                            {sup.name}
                          </p>
                        )}
                        {sup.cnpj && (
                          <p className="text-[10px] text-zinc-400 font-mono mt-0.5">
                            CNPJ: {sup.cnpj}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Endereço */}
                    {sup.address && (
                      <p className="text-[11px] text-zinc-400 flex items-center gap-1 line-clamp-1 font-medium">
                        <MapPin className="h-3 w-3 text-zinc-400 shrink-0" />
                        <span>{sup.address}</span>
                      </p>
                    )}
                  </div>

                  {/* Rodapé do Fornecedor */}
                  <div className="pt-3 border-t border-zinc-100 flex items-center justify-between text-xs">
                    <div>
                      <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Condições de Pagto</span>
                      <span className="font-bold text-zinc-800 text-[11px] truncate max-w-45 block">
                        {sup.paymentTerms || 'A combinar'}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                      {sup.phone && (
                        <button
                          type="button"
                          onClick={() => handleOpenWhatsApp(sup.phone, sup.tradeName || sup.name)}
                          className="p-2 bg-emerald-50 hover:bg-emerald-100 text-brand rounded-xl border border-emerald-100 transition-all cursor-pointer active:scale-95"
                          title="Fazer Cotação no WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setSelectedSupplier(sup)}
                        className="p-2 bg-zinc-50 hover:bg-zinc-100 text-zinc-600 rounded-xl border border-zinc-100 transition-all cursor-pointer active:scale-95"
                        title="Ver Ficha Cadastral"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* DRAWER / DETALHES DO FORNECEDOR                                          */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {selectedSupplier && (
        <div className="fixed inset-0 z-100 bg-neutral-950/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="relative w-full sm:max-w-xl bg-white border border-zinc-100 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-5 bg-brand text-white flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/20 text-white flex items-center justify-center font-black text-base border border-white/30">
                  {selectedSupplier.tradeName ? selectedSupplier.tradeName.substring(0, 2).toUpperCase() : selectedSupplier.name.substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-base font-black text-white">{selectedSupplier.tradeName || selectedSupplier.name}</h3>
                  {selectedSupplier.cnpj && (
                    <p className="text-xs text-white/80 font-mono">CNPJ: {selectedSupplier.cnpj}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                {onDeleteSupplier && (
                  <button
                    type="button"
                    onClick={() => handleDeleteSupplierAction(selectedSupplier.id, selectedSupplier.tradeName || selectedSupplier.name)}
                    className="p-2 text-white/80 hover:text-white hover:bg-red-600/50 rounded-xl transition-colors cursor-pointer"
                    title="Excluir Fornecedor"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleOpenEditModal(selectedSupplier)}
                  className="p-2 text-white/90 hover:text-white rounded-xl hover:bg-white/10 transition-colors cursor-pointer"
                  title="Editar Fornecedor"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSupplier(null)}
                  className="p-2 text-white/90 hover:text-white rounded-full hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-zinc-50 text-xs">
              {/* Dados Corporativos */}
              <div className="bg-white p-4 rounded-2xl border border-zinc-100 space-y-2.5 shadow-sm">
                <div className="flex items-center justify-between">
                  <h4 className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Dados Fiscais & Oficiais</h4>
                  <span className="text-[10px] font-bold text-brand bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100">
                    {selectedSupplier.category}
                  </span>
                </div>

                <div className="space-y-1.5 text-zinc-700">
                  <p><strong className="text-zinc-500">Razão Social:</strong> {selectedSupplier.name}</p>
                  {selectedSupplier.tradeName && <p><strong className="text-zinc-500">Nome Fantasia:</strong> {selectedSupplier.tradeName}</p>}
                  {selectedSupplier.cnae && <p><strong className="text-zinc-500">Atividade Principal (CNAE):</strong> {selectedSupplier.cnae}</p>}
                  {selectedSupplier.paymentTerms && <p><strong className="text-zinc-500">Condição Padrão:</strong> {selectedSupplier.paymentTerms}</p>}
                </div>
              </div>

              {/* Contatos & GPS */}
              <div className="bg-white p-4 rounded-2xl border border-zinc-100 space-y-3 shadow-sm">
                <h4 className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Contatos Comerciais & Endereço</h4>

                <div className="space-y-2">
                  {selectedSupplier.phone && (
                    <div className="flex items-center justify-between p-2.5 bg-zinc-50 rounded-xl border border-zinc-100">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-brand" />
                        <span className="font-bold">{selectedSupplier.phone}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpenWhatsApp(selectedSupplier.phone, selectedSupplier.tradeName || selectedSupplier.name)}
                        className="text-[10px] font-black bg-brand text-white px-3 py-1.5 rounded-lg hover:bg-brand-dark flex items-center gap-1 active:scale-95 transition-all cursor-pointer"
                      >
                        <MessageCircle className="h-3 w-3" />
                        <span>Fazer Cotação</span>
                      </button>
                    </div>
                  )}

                  {selectedSupplier.email && (
                    <div className="flex items-center gap-2 p-2.5 bg-zinc-50 rounded-xl border border-zinc-100">
                      <Mail className="h-4 w-4 text-zinc-400" />
                      <span>{selectedSupplier.email}</span>
                    </div>
                  )}

                  {selectedSupplier.address && (
                    <div className="p-2.5 bg-zinc-50 rounded-xl border border-zinc-100 space-y-2">
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
                        <span>{selectedSupplier.address}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpenGoogleMaps(selectedSupplier.address!)}
                        className="w-full py-2 bg-white hover:bg-zinc-100 text-zinc-700 font-bold text-xs rounded-xl border border-zinc-100 flex items-center justify-center gap-1.5 shadow-xs cursor-pointer active:scale-95 transition-all"
                      >
                        <ExternalLink className="h-3.5 w-3.5 text-brand" />
                        <span>Abrir no Google Maps</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Selo LGPD */}
              <div className="p-3 bg-emerald-50/60 border border-emerald-100 rounded-2xl flex items-center gap-2.5 text-[11px] text-emerald-900">
                <ShieldCheck className="h-5 w-5 text-brand shrink-0" />
                <span>Base pública da Receita Federal e dados cadastrados pelo usuário em conformidade com a LGPD (Lei 13.709/2018).</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL DE CADASTRO / EDIÇÃO COM ZERO DIGITAÇÃO POR CNPJ                   */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isEditModalOpen && editingSupplier && (
        <div className="fixed inset-0 z-110 bg-neutral-950/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
          <form onSubmit={handleSaveForm} className="relative w-full max-w-lg bg-white border border-zinc-100 rounded-3xl p-4 sm:p-6 space-y-4 shadow-2xl text-xs max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-brand" />
                <h3 className="text-sm font-black text-zinc-900">
                  {editingSupplier.name ? `Editar: ${editingSupplier.tradeName || editingSupplier.name}` : 'Cadastrar Fornecedor'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Banner de Busca de Fornecedor por CNPJ */}
            <div className="p-3 bg-zinc-50 border border-zinc-200/80 rounded-2xl space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-zinc-800 flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5 text-brand" />
                  Buscar Dados da Empresa pelo CNPJ
                </span>
                <span className="text-[9px] font-black uppercase tracking-wider bg-zinc-200/70 text-zinc-700 px-2 py-0.5 rounded-md">
                  Automático
                </span>
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="00.000.000/0001-00"
                  value={editingSupplier.cnpj || ''}
                  onChange={e => setEditingSupplier({ ...editingSupplier, cnpj: maskCnpj(e.target.value) })}
                  className="flex-1 min-w-0 bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs text-zinc-900 font-mono focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
                <button
                  type="button"
                  onClick={handleAutoFillCnpj}
                  disabled={isSearchingCnpj}
                  className="px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 text-white font-black text-xs rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95 shrink-0 whitespace-nowrap"
                >
                  {isSearchingCnpj ? (
                    <span>Buscando...</span>
                  ) : (
                    <>
                      <Search className="h-3.5 w-3.5 shrink-0" />
                      <span>Consultar</span>
                    </>
                  )}
                </button>
              </div>

              {autoFillMessage && (
                <div className={`p-2.5 rounded-xl text-[11px] font-medium flex items-center gap-1.5 ${
                  autoFillMessage.type === 'success' 
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                    : 'bg-rose-50 text-rose-800 border border-rose-200'
                }`}>
                  {autoFillMessage.type === 'success' ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 shrink-0" />}
                  <span>{autoFillMessage.text}</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                  Razão Social / Nome da Empresa *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Distribuidora de Peças Brasil LTDA"
                  value={editingSupplier.name || ''}
                  onChange={e => setEditingSupplier({ ...editingSupplier, name: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                    Nome Fantasia (Opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: AutoPeças SP"
                    value={editingSupplier.tradeName || ''}
                    onChange={e => setEditingSupplier({ ...editingSupplier, tradeName: e.target.value })}
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                    Categoria de Fornecimento
                  </label>
                  <select
                    value={editingSupplier.category || 'Autopeças & Mecânica'}
                    onChange={e => setEditingSupplier({ ...editingSupplier, category: e.target.value })}
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                  >
                    {SUPPLIER_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                    WhatsApp / Telefone
                  </label>
                  <input
                    type="text"
                    placeholder="(11) 99999-9999"
                    value={editingSupplier.phone || ''}
                    onChange={e => setEditingSupplier({ ...editingSupplier, phone: maskPhone(e.target.value) })}
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                    E-mail Comercial (Opcional)
                  </label>
                  <input
                    type="email"
                    placeholder="vendas@fornecedor.com.br"
                    value={editingSupplier.email || ''}
                    onChange={e => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                  Endereço Completo (Opcional)
                </label>
                <input
                  type="text"
                  placeholder="Rua, Número, Bairro, Cidade - UF"
                  value={editingSupplier.address || ''}
                  onChange={e => setEditingSupplier({ ...editingSupplier, address: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                    Condições de Pagamento (Opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Boleto 30 dias, PIX à vista ou cartão"
                    value={editingSupplier.paymentTerms || ''}
                    onChange={e => setEditingSupplier({ ...editingSupplier, paymentTerms: e.target.value })}
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                    Origem do Cadastro
                  </label>
                  <select
                    value={editingSupplier.source || 'manual'}
                    onChange={e => setEditingSupplier({ ...editingSupplier, source: e.target.value as SupplierSource })}
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl px-3.5 py-2.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium text-xs"
                  >
                    <option value="manual">Cadastro Manual</option>
                    <option value="cnpj_lookup">Cadastro Empresarial (CNPJ)</option>
                    <option value="nfe_upload">Upload de Nota Fiscal</option>
                    <option value="nfc_scanner">QR Code NFC-e</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">
                  Observações Internas (Opcional)
                </label>
                <textarea
                  rows={2}
                  placeholder="Nome do vendedor, prazos de entrega, descontos acordados..."
                  value={editingSupplier.notes || ''}
                  onChange={e => setEditingSupplier({ ...editingSupplier, notes: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl p-3 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20 resize-none font-medium text-xs"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
              {onDeleteSupplier && editingSupplier.id && suppliers.some(s => s.id === editingSupplier.id) ? (
                <button
                  type="button"
                  onClick={() => handleDeleteSupplierAction(editingSupplier.id!, editingSupplier.tradeName || editingSupplier.name)}
                  className="px-3.5 py-2 text-red-600 hover:bg-red-50 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Excluir Fornecedor</span>
                </button>
              ) : <div />}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-zinc-500 hover:text-zinc-800 font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 active:scale-95 transition-all cursor-pointer"
                >
                  Salvar Fornecedor
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
